import api from './api';

export const feedService = {
  getFeedPosts: async (page = 0, size = 20) => {
    try {
      const response = await api.get(`/posts/feed`, {
        params: {
          page,
          size,
          _t: Date.now()
        }
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        throw new Error('Please login to view your feed');
      }
      throw error.response?.data || { message: 'Failed to load feed. Please try again.' };
    }
  },

  getUserPosts: async (userId, page = 0, size = 20, category = null) => {
    try {
      const response = await api.get(`/posts/user/${userId}`, {
        params: {
          page,
          size,
          ...(category ? { category } : {}),
          _t: Date.now()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to load posts. Please try again.' };
    }
  },
  
  getUserPostsWithEchoes: async (userId, page = 0, size = 20) => {
    try {
      const response = await api.get(`/posts/user/${userId}/with-echoes`, {
        params: {
          page,
          size,
          _t: Date.now()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to load posts. Please try again.' };
    }
  },
  
  getUserEchoedPosts: async (userId, page = 0, size = 20) => {
    try {
      const response = await api.get(`/posts/user/${userId}/echoed`, {
        params: {
          page,
          size,
          _t: Date.now()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to load echoed posts. Please try again.' };
    }
  },
  
  getUserLikedPosts: async (userId, page = 0, size = 20) => {
    try {
      const response = await api.get(`/posts/user/${userId}/likes`, {
        params: {
          page,
          size,
          _t: Date.now()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to load liked posts. Please try again.' };
    }
  },
  
  getUserMediaPosts: async (userId, page = 0, size = 20, mediaType = 'all') => {
    try {
      const response = await api.get(`/posts/user/${userId}/media`, {
        params: {
          page,
          size,
          mediaType,
          _t: Date.now()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to load media posts. Please try again.' };
    }
  },
  
  getUserForSalePosts: async (userId, page = 0, size = 20) => {
    try {
      const response = await api.get(`/posts/user/${userId}/for-sale`, {
        params: {
          page,
          size,
          _t: Date.now()
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to load for-sale posts. Please try again.' };
    }
  },
  
  createPost: async (formData) => {
    try {
      const response = await api.post('/posts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      // Extract specific error message from backend response
      const errorData = error.response?.data;
      if (errorData?.message) {
        throw new Error(errorData.message);
      }
      throw new Error(error.response?.statusText || 'Failed to create post. Please try again.');
    }
  },
  
  likePost: async (postId) => {
    try {
      const response = await api.post(`/posts/${postId}/like`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to like post. Please try again.' };
    }
  },

  unlikePost: async (postId) => {
    try {
      const response = await api.delete(`/posts/${postId}/like`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to unlike post. Please try again.' };
    }
  },

  deletePost: async (postId) => {
    try {
      await api.delete(`/posts/${postId}`);
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete post. Please try again.' };
    }
  },

  updatePost: async (postId, formData) => {
    try {
      const response = await api.put(`/posts/${postId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      // Extract specific error message from backend response
      const errorData = error.response?.data;
      if (errorData?.message) {
        throw new Error(errorData.message);
      }
      throw new Error(error.response?.statusText || 'Failed to update post. Please try again.');
    }
  },

  getPost: async (postId) => {
    try {
      const response = await api.get(`/posts/${postId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to load post. Please try again.' };
    }
  },

  // Poll voting functions
  voteOnPoll: async (postId, selectedOptionIndex) => {
    try {
      const response = await api.post(`/polls/${postId}/vote`, {
        selectedOptionIndex
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to vote on poll. Please try again.' };
    }
  },

  removeVoteFromPoll: async (postId, selectedOptionIndex) => {
    try {
      await api.delete(`/polls/${postId}/vote`, {
        data: { selectedOptionIndex }
      });
    } catch (error) {
      throw error.response?.data || { message: 'Failed to remove vote. Please try again.' };
    }
  },

  getPollResults: async (postId) => {
    try {
      const response = await api.get(`/polls/${postId}/results`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to load poll results. Please try again.' };
    }
  },

  pinPost: async (postId) => {
    try {
      const response = await api.post(`/posts/${postId}/pin`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to pin post. Please try again.' };
    }
  },

  // Replaces the post's categories (names); returns the saved categories
  updatePostCategories: async (postId, categories) => {
    try {
      const response = await api.put(`/posts/${postId}/categories`, { categories });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update categories. Please try again.' };
    }
  },

  unpinPost: async (postId) => {
    try {
      const response = await api.delete(`/posts/${postId}/pin`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to unpin post. Please try again.' };
    }
  },

  // Reshare functions
  resharePost: async (postId) => {
    try {
      const response = await api.post(`/posts/${postId}/reshare`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to reshare post. Please try again.' };
    }
  },

  undoReshare: async (postId) => {
    try {
      await api.delete(`/posts/${postId}/reshare`);
    } catch (error) {
      throw error.response?.data || { message: 'Failed to undo reshare. Please try again.' };
    }
  },

  hasReshared: async (postId) => {
    try {
      const response = await api.get(`/posts/${postId}/reshare/status`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to check reshare status. Please try again.' };
    }
  },

  // ---- Scheduled Posts ----
  getScheduledPosts: async () => {
    try {
      const response = await api.get('/posts/scheduled');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to load scheduled posts.' };
    }
  },

  cancelScheduledPost: async (postId) => {
    try {
      await api.delete(`/posts/${postId}/schedule`);
    } catch (error) {
      throw error.response?.data || { message: 'Failed to cancel scheduled post.' };
    }
  },

  reschedulePost: async (postId, scheduledFor) => {
    try {
      const response = await api.patch(`/posts/${postId}/schedule`, { scheduledFor });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to reschedule post.' };
    }
  },
};